import type { SceneEntity } from '@seddi/scene';
import type { SceneDesc } from '../domain/scene/sceneTypes';
import type { SceneRenderable } from '../renderables/sceneRenderable';
import type { GarmentGeometryGfx } from '../resources/garmentGeometryTypes';
import type { ResourcesContext } from '../resources/resourcesContext';
import type { StitchesTextures } from '../resources/stitchesTexturesTypes';
import type { Theme } from '../theme/themeTypes';
import type { SceneReconcilerConfig } from './sceneReconcilerConfig';

export type SceneReconcilerFrameEntity = {
  childrenDiff: { prev?: SceneEntity; next: SceneEntity }[];
  data: SceneEntity;
  isGeometryDirty?: boolean;
  isPiece?: boolean;
};

export type SceneReconcilerFrame = {
  readonly config: SceneReconcilerConfig;
  readonly entities: Map<string, SceneReconcilerFrameEntity>;
  nextGarmentGeometry?: GarmentGeometryGfx;
  nextParent?: SceneEntity;
  nextScene?: SceneDesc;
  nextUseGarmentGeometry: boolean;
  prevGarmentGeometry?: GarmentGeometryGfx;
  prevParent?: SceneEntity;
  prevScene?: SceneDesc;
  prevUseGarmentGeometry: boolean;
  readonly resources: ResourcesContext;
  readonly scene: SceneRenderable;
  stitchesTextures?: StitchesTextures;
  readonly theme: Theme;
  useBrandLogos: boolean;
};

/**
 * Create.
 * @param config - Config.
 * @param resources - Resources context.
 * @param scene - Scene renderable.
 * @param theme - Theme.
 */
export const create = (
  config: SceneReconcilerConfig,
  resources: ResourcesContext,
  scene: SceneRenderable,
  theme: Theme,
): SceneReconcilerFrame => {
  return {
    config,
    entities: new Map(),
    nextGarmentGeometry: undefined,
    nextParent: undefined,
    nextScene: undefined,
    nextUseGarmentGeometry: false,
    prevGarmentGeometry: undefined,
    prevParent: undefined,
    prevScene: undefined,
    prevUseGarmentGeometry: false,
    resources,
    scene,
    theme,
    useBrandLogos: false,
  };
};

/**
 * Reset at the beginning of each frame.
 * @param self - Scene reconciler frame.
 */
export const reset = (self: SceneReconcilerFrame) => {
  // Swap states.
  self.prevGarmentGeometry = self.nextGarmentGeometry;
  self.prevParent = self.nextParent;
  self.prevScene = self.nextScene;
  self.prevUseGarmentGeometry = self.nextUseGarmentGeometry;
  // Reset props.
  self.entities.clear();
  self.nextGarmentGeometry = undefined;
  self.nextParent = undefined;
  self.nextScene = undefined;
  self.nextUseGarmentGeometry = false;
  self.stitchesTextures = undefined;
};
